import { usePaymentMethods } from '@/api/customers/payment-methods/getPaymentMethods';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/elements/accordion';
import { LocationContext } from '@/providers/location';
import { Customer, ModelID } from '@/types';
import { dateFromApiDateTime } from '@/utils/format';
import { formatDistanceToNow } from 'date-fns';
import { useContext } from 'react';
import { EditPaymentMethod } from './EditPaymentMethod';
import { NewPaymentMethod } from './NewPaymentMethod';

interface PaymentMethodManagerProps {
  customer: Customer;
  locationId: ModelID;
}

const AccordionItemButtonStyle =
  'flex w-full justify-between bg-background border border-b-0 px-4 py-2 text-left text-sm font-medium text-foreground/95 hover:bg-background/20 focus:outline-hidden focus-visible:ring-3 focus-visible:ring-gray-500 focus-visible:ring-opacity-75';
const AccordionItemPanelStyle = 'text-sm border border-t-0 bg-background/10 p-2';

const PaymentMethodManager = ({ customer, locationId }: PaymentMethodManagerProps) => {
  const { activeLocationId } = useContext(LocationContext);
  const { data: paymentMethods } = usePaymentMethods({
    location: activeLocationId,
    search: { customer_id: customer.id },
  });

  return (
    <Accordion type="multiple">
      {paymentMethods?.map((paymentMethod, index) => (
        <AccordionItem
          key={`payment-${index}`}
          value={`payment-${paymentMethod.payment_method_id}`}
          className="data-[state=open]:my-3"
        >
          <AccordionTrigger className={AccordionItemButtonStyle}>
            {paymentMethod.brand === 'unknown' || paymentMethod.brand === 'link' ? (
              <>
                <div className="px-2">Link secured payment</div>
              </>
            ) : (
              <>
                <div className="px-2">**** **** **** {paymentMethod.last4}</div>
                <div className="grow">
                  {paymentMethod.exp_month} / {paymentMethod.exp_year}
                </div>
              </>
            )}
            <div className="text-sm px-1">
              Added:{' '}
              {formatDistanceToNow(dateFromApiDateTime(paymentMethod.created_at), {
                includeSeconds: true,
                addSuffix: true,
              })}
            </div>
          </AccordionTrigger>
          <AccordionContent className={AccordionItemPanelStyle}>
            <EditPaymentMethod paymentMethod={paymentMethod} />
          </AccordionContent>
        </AccordionItem>
      ))}
      <AccordionItem value="new-payment-method">
        <AccordionTrigger className={`${AccordionItemButtonStyle} border-b`}>
          New Payment Method
        </AccordionTrigger>
        <AccordionContent className={AccordionItemPanelStyle}>
          <NewPaymentMethod
            customer={customer}
            franchiseId={locationId}
            defaultOpen={!paymentMethods || paymentMethods.length < 1}
          />
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

export default PaymentMethodManager;
